import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, captureCsrfToken } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import { useWsStatus } from '@/contexts/ws-status-context';
import { WS_CONNECTED_POLL_INTERVAL, WS_DISCONNECTED_POLL_INTERVAL } from '@/hooks/use-realtime-notifications';
import type {
  PlatformDashboardData,
  GlobalSearchResult,
  NotificationsResponse,
  AlertsSummaryResponse,
  CrossModuleProjectSafety,
  CrossModuleProjectGear,
  CrossModuleProjectCrew,
  PlatformGearModelListItem,
  PlatformGearModelDetail,
  PlatformGearModelCreatePayload,
  PlatformGearModelUpdatePayload,
  PlatformGearModelDuplicateGroup,
  PlatformGearModelPromotePayload,
  PlatformGearModelPromoteResponse,
  InspectionStepTemplate,
  InspectionStepCreatePayload,
  InspectionStepUpdatePayload,
  PaginatedResponse,
  Manufacturer,
  PlatformStats,
  PlatformOrgActivity,
  PlatformOrgActivityTable,
  PlatformOrganizationListItem,
  PlatformOrganizationDetail,
  PlatformSubscription,
  PlatformStartTrialPayload,
  PlatformOrgUsersResponse,
  PlatformOrgUser,
  PlatformOrgModulesResponse,
  PlatformImpersonateResponse,
  PlatformUserUpdatePayload,
  PlatformResetPasswordPayload,
  PlatformSubscriptionUpdatePayload,
  PlatformOrganizationCreatePayload,
  PlatformOrganizationCreateResponse,
  PlatformOrganizationWizardUpdatePayload,
  SubscriptionPlan,
} from '@/lib/types';

const FIVE_MINUTES = 1000 * 60 * 5;
const ONE_MINUTE = 1000 * 60;

// ─── Platform Dashboard ──────────────────────────────────────────────────────

export function usePlatformDashboard() {
  return useQuery({
    queryKey: queryKeys.dashboard.platform(),
    queryFn: async (): Promise<PlatformDashboardData> => {
      const response = await apiClient.get<PlatformDashboardData>('/api/v1/dashboard/');
      return response.data;
    },
    refetchInterval: FIVE_MINUTES,
    staleTime: FIVE_MINUTES,
  });
}

// ─── Global Search ───────────────────────────────────────────────────────────

export function useGlobalSearch(query: string) {
  return useQuery({
    queryKey: queryKeys.search.global(query),
    queryFn: async (): Promise<GlobalSearchResult> => {
      const response = await apiClient.get<GlobalSearchResult>('/api/v1/search/', {
        params: { q: query },
      });
      return response.data;
    },
    enabled: query.trim().length >= 2,
    staleTime: ONE_MINUTE,
  });
}

// ─── Notifications ───────────────────────────────────────────────────────────

export function useNotifications() {
  const { wsConnected } = useWsStatus();
  const pollInterval = wsConnected ? WS_CONNECTED_POLL_INTERVAL : WS_DISCONNECTED_POLL_INTERVAL;

  return useQuery({
    queryKey: queryKeys.notifications.list(),
    queryFn: async (): Promise<NotificationsResponse> => {
      const response = await apiClient.get<NotificationsResponse>('/api/v1/notifications/');
      return response.data;
    },
    refetchInterval: pollInterval,
    staleTime: pollInterval,
  });
}

export function useUnreadNotificationCount() {
  const { wsConnected } = useWsStatus();
  const pollInterval = wsConnected ? WS_CONNECTED_POLL_INTERVAL : WS_DISCONNECTED_POLL_INTERVAL;

  return useQuery({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: async (): Promise<number> => {
      const response = await apiClient.get<{ unread_count: number }>('/api/v1/notifications/unread-count/');
      return response.data.unread_count;
    },
    refetchInterval: pollInterval,
    staleTime: Math.floor(pollInterval / 2),
  });
}

export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { ids?: number[]; all?: boolean; types?: string[] }) => {
      const response = await apiClient.post('/api/v1/notifications/mark-read/', payload);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

// ─── Alerts ─────────────────────────────────────────────────────────────────

export function useAlerts(params?: {
  type?: string;
  is_read?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: [...queryKeys.notifications.all, 'alerts', params] as const,
    queryFn: async (): Promise<NotificationsResponse> => {
      const response = await apiClient.get<NotificationsResponse>('/api/v1/notifications/', {
        params: { alerts_only: 'true', ...params },
      });
      return response.data;
    },
    refetchInterval: ONE_MINUTE,
    staleTime: 30_000,
  });
}

export function useAlertsSummary() {
  return useQuery({
    queryKey: [...queryKeys.notifications.all, 'alerts-summary'] as const,
    queryFn: async (): Promise<AlertsSummaryResponse> => {
      const response = await apiClient.get<AlertsSummaryResponse>('/api/v1/notifications/alerts-summary/');
      return response.data;
    },
    refetchInterval: ONE_MINUTE,
    staleTime: 30_000,
  });
}

// ─── Cross-Module: Project ───────────────────────────────────────────────────

export function useProjectSafetyData(projectId: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.crossModule.projectSafety(projectId),
    queryFn: async (): Promise<CrossModuleProjectSafety> => {
      const response = await apiClient.get<CrossModuleProjectSafety>(
        `/api/v1/x-project/${projectId}/safety/`
      );
      return response.data;
    },
    enabled: projectId > 0 && enabled,
    staleTime: FIVE_MINUTES,
  });
}

export function useProjectGearData(projectId: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.crossModule.projectGear(projectId),
    queryFn: async (): Promise<CrossModuleProjectGear> => {
      const response = await apiClient.get<CrossModuleProjectGear>(
        `/api/v1/x-project/${projectId}/gear/`
      );
      return response.data;
    },
    enabled: projectId > 0 && enabled,
    staleTime: FIVE_MINUTES,
  });
}

export function useProjectCrewData(projectId: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.crossModule.projectCrew(projectId),
    queryFn: async (): Promise<CrossModuleProjectCrew> => {
      const response = await apiClient.get<CrossModuleProjectCrew>(
        `/api/v1/x-project/${projectId}/crew/`
      );
      return response.data;
    },
    enabled: projectId > 0 && enabled,
    staleTime: FIVE_MINUTES,
  });
}

// ─── Cross-Module: Tech ──────────────────────────────────────────────────────

export function useTechProjectsData(techId: number) {
  return useQuery({
    queryKey: queryKeys.crossModule.techProjects(techId),
    queryFn: async () => {
      const response = await apiClient.get(`/api/v1/x-tech/${techId}/projects/`);
      return response.data;
    },
    enabled: techId > 0,
    staleTime: FIVE_MINUTES,
  });
}

export function useTechSafetyData(techId: number) {
  return useQuery({
    queryKey: queryKeys.crossModule.techSafety(techId),
    queryFn: async () => {
      const response = await apiClient.get(`/api/v1/x-tech/${techId}/safety/`);
      return response.data;
    },
    enabled: techId > 0,
    staleTime: FIVE_MINUTES,
  });
}

// ─── Platform Admin: Gear Models ─────────────────────────────────────────────

export function usePlatformGearModels(params?: {
  search?: string;
  asset_category?: string;
  page?: number;
  ordering?: string;
}) {
  return useQuery({
    queryKey: queryKeys.gearModels.list(params ?? {}),
    queryFn: async (): Promise<PaginatedResponse<PlatformGearModelListItem>> => {
      const response = await apiClient.get<PaginatedResponse<PlatformGearModelListItem>>(
        '/api/v1/platform/gear-models/',
        { params }
      );
      return response.data;
    },
    staleTime: ONE_MINUTE,
  });
}

export function usePlatformGearModel(id: number) {
  return useQuery({
    queryKey: queryKeys.gearModels.detail(id),
    queryFn: async (): Promise<PlatformGearModelDetail> => {
      const response = await apiClient.get<PlatformGearModelDetail>(
        `/api/v1/platform/gear-models/${id}/`
      );
      return response.data;
    },
    enabled: id > 0,
    staleTime: ONE_MINUTE,
  });
}

export function useCreatePlatformGearModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: PlatformGearModelCreatePayload) => {
      const response = await apiClient.post<PlatformGearModelDetail>(
        '/api/v1/platform/gear-models/',
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.gearModels.all });
    },
  });
}

export function useUpdatePlatformGearModel(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: PlatformGearModelUpdatePayload) => {
      const response = await apiClient.patch<PlatformGearModelDetail>(
        `/api/v1/platform/gear-models/${id}/`,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.gearModels.all });
    },
  });
}

export function useDeletePlatformGearModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/v1/platform/gear-models/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.gearModels.all });
    },
  });
}

export function usePlatformCustomGearModelGroups(params?: {
  appears_in_2plus?: boolean;
  asset_category?: string;
}) {
  return useQuery({
    queryKey: [...queryKeys.gearModels.all, 'duplicates', params ?? {}],
    queryFn: async (): Promise<PlatformGearModelDuplicateGroup[]> => {
      const response = await apiClient.get<PlatformGearModelDuplicateGroup[]>(
        '/api/v1/platform/gear-models/duplicates/',
        { params }
      );
      return response.data;
    },
  });
}

export function usePromoteGearModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: PlatformGearModelPromotePayload): Promise<PlatformGearModelPromoteResponse> => {
      const response = await apiClient.post<PlatformGearModelPromoteResponse>(
        '/api/v1/platform/gear-models/promote/',
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.gearModels.all });
    },
  });
}

// ─── Platform Admin: Inspection Step Templates ───────────────────────────────

export function usePlatformInspectionSteps(gearModelId: number) {
  return useQuery({
    queryKey: [...queryKeys.gearModels.detail(gearModelId), 'steps'] as const,
    queryFn: async (): Promise<InspectionStepTemplate[]> => {
      const response = await apiClient.get<InspectionStepTemplate[]>(
        `/api/v1/platform/gear-models/${gearModelId}/inspection-steps/`
      );
      return response.data;
    },
    enabled: gearModelId > 0,
    staleTime: ONE_MINUTE,
  });
}

export function useCreatePlatformInspectionStep(gearModelId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: InspectionStepCreatePayload) => {
      const response = await apiClient.post<InspectionStepTemplate>(
        `/api/v1/platform/gear-models/${gearModelId}/inspection-steps/`,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.gearModels.detail(gearModelId) });
    },
  });
}

export function useUpdatePlatformInspectionStep(gearModelId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ stepId, ...payload }: InspectionStepUpdatePayload & { stepId: number }) => {
      const response = await apiClient.patch<InspectionStepTemplate>(
        `/api/v1/platform/gear-models/${gearModelId}/inspection-steps/${stepId}/`,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.gearModels.detail(gearModelId) });
    },
  });
}

export function useDeletePlatformInspectionStep(gearModelId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (stepId: number) => {
      await apiClient.delete(
        `/api/v1/platform/gear-models/${gearModelId}/inspection-steps/${stepId}/`
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.gearModels.detail(gearModelId) });
    },
  });
}

export function useCopyInspectionStepsFromCategory(gearModelId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<InspectionStepTemplate[]>(
        `/api/v1/platform/gear-models/${gearModelId}/inspection-steps/copy-from-category/`
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.gearModels.detail(gearModelId) });
    },
  });
}

export function usePlatformManufacturers() {
  return useQuery({
    queryKey: queryKeys.manufacturers.all,
    queryFn: async (): Promise<Manufacturer[]> => {
      const response = await apiClient.get<PaginatedResponse<Manufacturer>>(
        '/api/v1/manufacturers/',
        { params: { page_size: 500 } }
      );
      return response.data.results;
    },
    staleTime: FIVE_MINUTES,
  });
}

// ─── Platform Admin: Stats & Organizations ──────────────────────────────────

export function usePlatformStats() {
  return useQuery({
    queryKey: queryKeys.platformAdmin.stats(),
    queryFn: async (): Promise<PlatformStats> => {
      const response = await apiClient.get<PlatformStats>('/api/v1/platform/stats/');
      return response.data;
    },
    staleTime: FIVE_MINUTES,
    refetchInterval: FIVE_MINUTES,
  });
}

export function usePlatformOrgActivity(orgId: number, params: {
  page: number;
  module?: string;
  user?: string;
  show_system: boolean;
}) {
  return useQuery({
    queryKey: [...queryKeys.platformAdmin.organizationDetail(orgId), 'activity', params],
    queryFn: async (): Promise<PlatformOrgActivity> => {
      const response = await apiClient.get<PlatformOrgActivity>(`/api/v1/platform/organizations/${orgId}/activity/`, { params });
      return response.data;
    },
    enabled: orgId > 0,
    staleTime: 0,
  });
}

export function usePlatformOrgActivityTable() {
  return useQuery({
    queryKey: [...queryKeys.platformAdmin.stats(), 'org-activity'],
    queryFn: async (): Promise<PlatformOrgActivityTable> => {
      const response = await apiClient.get<PlatformOrgActivityTable>('/api/v1/platform/stats/org-activity/');
      return response.data;
    },
    staleTime: ONE_MINUTE,
    refetchInterval: ONE_MINUTE,
  });
}

export function usePlatformOrganizations(params?: {
  search?: string;
  is_active?: string;
  trial_expiring_within?: string;
  page?: number;
  ordering?: string;
}) {
  return useQuery({
    queryKey: queryKeys.platformAdmin.organizationList(params ?? {}),
    queryFn: async (): Promise<PaginatedResponse<PlatformOrganizationListItem>> => {
      const response = await apiClient.get<PaginatedResponse<PlatformOrganizationListItem>>(
        '/api/v1/platform/organizations/',
        { params }
      );
      return response.data;
    },
    staleTime: ONE_MINUTE,
  });
}

export function usePlatformOrgSubscription(orgId: number) {
  return useQuery({
    queryKey: [...queryKeys.platformAdmin.all, 'subscription', orgId] as const,
    queryFn: async (): Promise<PlatformSubscription> => {
      const response = await apiClient.get<PlatformSubscription>(
        `/api/v1/platform/organizations/${orgId}/subscription/`
      );
      return response.data;
    },
    enabled: orgId > 0,
    staleTime: ONE_MINUTE,
  });
}

export function useCreatePlatformOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: PlatformOrganizationCreatePayload) => {
      const response = await apiClient.post<PlatformOrganizationCreateResponse>(
        '/api/v1/platform/organizations/',
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.platformAdmin.all });
    },
  });
}

export function useWizardUpdateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      ...payload
    }: PlatformOrganizationWizardUpdatePayload & { orgId: number }) => {
      const response = await apiClient.post<PlatformOrganizationCreateResponse>(
        `/api/v1/platform/organizations/${orgId}/wizard-update/`,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.platformAdmin.all });
    },
  });
}

export function useExtendTrial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orgId, ...payload }: PlatformStartTrialPayload & { orgId: number }) => {
      const response = await apiClient.post<PlatformSubscription>(
        `/api/v1/platform/organizations/${orgId}/subscription/start-trial/`,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.platformAdmin.all });
    },
  });
}

export function useSyncAllModules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ synced: number; errors: string[] }> => {
      const response = await apiClient.post<{ synced: number; errors: string[] }>(
        '/api/v1/platform/stats/sync-all-modules/'
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.platformAdmin.all });
    },
  });
}

// ─── Platform Admin: Organization Detail ────────────────────────────────────

export function usePlatformOrganization(id: number) {
  return useQuery({
    queryKey: queryKeys.platformAdmin.organizationDetail(id),
    queryFn: async (): Promise<PlatformOrganizationDetail> => {
      const response = await apiClient.get<PlatformOrganizationDetail>(
        `/api/v1/platform/organizations/${id}/`
      );
      return response.data;
    },
    enabled: id > 0,
    staleTime: ONE_MINUTE,
  });
}

export function useUpdatePlatformOrganization(defaultId?: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      organizationId,
      ...payload
    }: {
      organizationId?: number;
      name?: string;
      industry?: string;
      is_active?: boolean;
      is_demo?: boolean;
      demo_confirmation?: string;
    }) => {
      const id = organizationId ?? defaultId;
      if (!id) {
        throw new Error('Organization ID is required');
      }
      const response = await apiClient.patch<PlatformOrganizationDetail>(
        `/api/v1/platform/organizations/${id}/`,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.platformAdmin.all });
    },
  });
}

export function useResendPlatformAdminInvitation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<{
        admin_invitation: PlatformOrganizationDetail['admin_invitation'];
        email_configured: boolean;
        invitation_email_dispatched: boolean;
      }>(`/api/v1/platform/organizations/${id}/admin-invitation/resend/`);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.platformAdmin.organizationDetail(id) });
    },
  });
}

export function useDeleteOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      confirmation,
    }: {
      id: number;
      confirmation: string;
    }) => {
      await apiClient.delete(`/api/v1/platform/organizations/${id}/`, {
        data: { confirmation },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.platformAdmin.all });
    },
  });
}

// ─── Platform Admin: Organization Users ─────────────────────────────────────

export function usePlatformOrgUsers(orgId: number) {
  return useQuery({
    queryKey: queryKeys.platformAdmin.orgUsers(orgId),
    queryFn: async (): Promise<PlatformOrgUsersResponse> => {
      const response = await apiClient.get<PlatformOrgUsersResponse>(
        `/api/v1/platform/organizations/${orgId}/users/`
      );
      return response.data;
    },
    enabled: orgId > 0,
    staleTime: ONE_MINUTE,
  });
}

export function useUpdatePlatformOrgUser(orgId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      ...payload
    }: PlatformUserUpdatePayload & {
      userId: number;
      confirm_admin_grant?: boolean;
    }) => {
      const response = await apiClient.patch<PlatformOrgUser>(
        `/api/v1/platform/organizations/${orgId}/users/${userId}/`,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.platformAdmin.orgUsers(orgId) });
    },
  });
}

export function useResetPlatformUserPassword(orgId: number) {
  return useMutation({
    mutationFn: async ({ userId, ...payload }: PlatformResetPasswordPayload & { userId: number }) => {
      const response = await apiClient.post(
        `/api/v1/platform/organizations/${orgId}/users/${userId}/reset-password/`,
        payload
      );
      return response.data;
    },
  });
}

export function useImpersonateUser(orgId: number) {
  return useMutation({
    mutationFn: async ({ userId, step_up_token }: { userId: number; step_up_token: string }) => {
      const response = await apiClient.post<PlatformImpersonateResponse>(
        `/api/v1/platform/organizations/${orgId}/users/${userId}/impersonate/`,
        { step_up_token }
      );
      captureCsrfToken(response.data, response.headers);
      return response.data;
    },
  });
}

export function useNukePlatformUser(orgId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) => {
      const response = await apiClient.delete(
        `/api/v1/platform/organizations/${orgId}/users/${userId}/nuke/`
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.platformAdmin.orgUsers(orgId) });
      qc.invalidateQueries({ queryKey: queryKeys.platformAdmin.organizationDetail(orgId) });
    },
  });
}

// ─── Platform Admin: Organization Modules ───────────────────────────────────

export function usePlatformOrgModules(orgId: number) {
  return useQuery({
    queryKey: queryKeys.platformAdmin.orgModules(orgId),
    queryFn: async (): Promise<PlatformOrgModulesResponse> => {
      const response = await apiClient.get<PlatformOrgModulesResponse>(
        `/api/v1/platform/organizations/${orgId}/modules/`
      );
      return response.data;
    },
    enabled: orgId > 0,
    staleTime: ONE_MINUTE,
  });
}

export function useUpdatePlatformOrgModules(orgId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (modules: Record<string, boolean>) => {
      const response = await apiClient.patch<PlatformOrgModulesResponse>(
        `/api/v1/platform/organizations/${orgId}/modules/`,
        { modules }
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.platformAdmin.orgModules(orgId) });
      qc.invalidateQueries({ queryKey: queryKeys.platformAdmin.organizationDetail(orgId) });
    },
  });
}

// ─── Platform Admin: Sync Modules ────────────────────────────────────────────

export function useSyncPlatformOrgModules(orgId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<PlatformOrgModulesResponse>(
        `/api/v1/platform/organizations/${orgId}/subscription/sync-modules/`
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.platformAdmin.orgModules(orgId) });
      qc.invalidateQueries({ queryKey: queryKeys.platformAdmin.organizationDetail(orgId) });
    },
  });
}

// ─── Platform Admin: Billing Plans (for org creation wizard) ─────────────────

export function usePlatformBillingPlans() {
  return useQuery({
    queryKey: queryKeys.platformAdmin.plans(),
    queryFn: async (): Promise<SubscriptionPlan[]> => {
      const response = await apiClient.get<SubscriptionPlan[]>(
        '/api/v1/platform/plans/'
      );
      return response.data;
    },
    staleTime: FIVE_MINUTES,
  });
}

// ─── Platform Admin: Subscription Update ────────────────────────────────────

export function useUpdatePlatformSubscription(orgId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: PlatformSubscriptionUpdatePayload) => {
      const response = await apiClient.patch<PlatformSubscription>(
        `/api/v1/platform/organizations/${orgId}/subscription/`,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.platformAdmin.all });
    },
  });
}
